import { z } from 'zod';
import type { InspectionReportRequest } from '../hooks/useUrgently';

/**
 * Urgently's two inspections of a job's vehicle: where it was picked up,
 * and where it was dropped off. The partner API's names.
 */
export type UrgentlyInspectionKind = 'ON_SITE' | 'ON_DROP_OFF';

export const URGENTLY_INSPECTION_TITLES: Record<
  UrgentlyInspectionKind,
  string
> = {
  ON_SITE: 'On site inspection',
  ON_DROP_OFF: 'Drop off inspection',
};

/** What each photo shows, as Urgently files it. */
export const URGENTLY_DOCUMENT_TYPES = [
  { value: 'CUSTOMER_VEHICLE_PHOTO', label: 'Vehicle photo' },
  { value: 'LICENSE_PLATE_PHOTO', label: 'License plate' },
  { value: 'CUSTOMER_SIGNATURE', label: 'Customer signature' },
  { value: 'RECEIPT', label: 'Receipt' },
  { value: 'OTHER', label: 'Other' },
] as const;

export type UrgentlyDocumentType =
  (typeof URGENTLY_DOCUMENT_TYPES)[number]['value'];

const DOCUMENT_TYPE_VALUES = URGENTLY_DOCUMENT_TYPES.map(
  (type) => type.value
) as [UrgentlyDocumentType, ...UrgentlyDocumentType[]];

/**
 * An inspection as the sheet asks it: whether the vehicle is damaged,
 * anything to add, and the photos, each saying what it shows. Nothing is
 * required, as on the old screen: a driver may report an undamaged vehicle
 * with no photo.
 */
export const urgentlyInspectionSchema = z.object({
  vehicleDamaged: z.boolean().default(false),
  notes: z.string(),
  photos: z.array(
    z.object({
      id: z.string(),
      file: z.custom<Blob>((value) => value instanceof Blob),
      // Chosen before the photo is taken, so never empty.
      documentType: z
        .enum(DOCUMENT_TYPE_VALUES)
        .default('CUSTOMER_VEHICLE_PHOTO'),
      capturedAt: z.string(),
    })
  ),
});

export type UrgentlyInspectionValues = z.infer<typeof urgentlyInspectionSchema>;

/**
 * The request POST /dispatches/:id/urgently/inspection-report takes. Each
 * photo goes as a base64 `data:` URL, read by `toDataUrl`, with where the
 * phone was: the server requires a position on every photo, and sends 0, 0
 * when the phone would not say, as the old screen did.
 */
export function toInspectionReport(
  kind: UrgentlyInspectionKind,
  values: UrgentlyInspectionValues,
  dataUrls: string[],
  position: { latitude: number; longitude: number } | null
): InspectionReportRequest {
  const latitude = position?.latitude ?? 0;
  const longitude = position?.longitude ?? 0;
  return {
    inspectionType: kind,
    vehicleDamaged: values.vehicleDamaged,
    latitude: position?.latitude,
    longitude: position?.longitude,
    notes: values.notes.trim() || undefined,
    photos: values.photos.map((photo, index) => ({
      dataUrl: dataUrls[index],
      latitude,
      longitude,
      captureTime: photo.capturedAt,
      documentType: photo.documentType,
    })),
    documentIds: [],
  };
}

/**
 * Whether the photos fit in one request: the server takes 10 MB of JSON,
 * and the report's other fields and the JSON around each photo need some
 * of it. Scaled down (toPhotoDataUrl), a photo is a few hundred KB, so
 * this stops only an inspection with dozens; refused by the server, it
 * read as a failure with no reason.
 */
export const fitsOneRequest = (dataUrls: string[]) =>
  dataUrls.reduce((total, url) => total + url.length, 0) <= 9_000_000;
